<template>
  <div class="flex flex-col flex-auto text-center">
    <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
      {{ errorMessage }}
    </div>
    <div v-else-if="isLoading">
      <LayupSpinner />
      <p class="text-black">Loading...</p>
    </div>
    <div v-else class="flex flex-col flex-auto justify-between">
      <div class="w-full">
        <div class="h-min-max">
          <div class="my-2">
            <WidgetMerchantLogo />
            <p class="text-base-black my-3 text-sm font-medium">Total: {{ currency(widgetStore.order.amountDue) }}</p>
          </div>

          <p class="font-semibold text-3xl text-base-black my-3">{{ instalmentAmount }} pm</p>

          <SelectInput
            :label="`Select monthly payment date`"
            :selections="monthlyBillingDays"
            v-model="billingDay"
            data-testid="billingDay"
          />

          <SelectInput
            :label="`Select duration`"
            :selections="durationOptions"
            v-model="duration"
            data-testid="duration"
          />

          <div class="flex place-content-center w-auto">
            <p class="text-base-black text-sm font-medium">
              Deposit amount
              <span class="text-brand-700 mr-2 font-bold">{{ depositDue }}</span>
            </p>
          </div>

          <div class="flex justify-center items-center my-4">
            <Button
              variant="outline"
              @click="router.push({ name: 'PaymentPlanBreakdown' })"
              data-testid="paymentPlanBreakdown"
            >
              Payment breakdown <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
      <div>
        <div class="flex text-left px-2 gap-2 items-center">
          <CheckboxComponent v-model="acceptTermsAndConditions" name="acceptTermsAndConditions" id="terms-checkbox" />
          <label class="break-words text-sm text-gray-500" for="terms-checkbox">
            You agree to
            <a
              href="#"
              class="text-brand-800 underline hover:text-brand-500"
              color="text-brand-700"
              target="_blank"
              @click="
                onDownloadPrivacyTerms(
                  'https://res.cloudinary.com/layup/image/upload/v1726666846/LayUp_User_Terms_and_Conditions_pzbn86.pdf',
                )
              "
              >LayUp's</a
            >
            <span v-if="widgetStore.order.merchantTermsLink"
              >and
              <a
                href="#"
                class="text-brand-800 underline hover:text-brand-500"
                color="text-brand-700"
                target="_blank"
                @click="onDownloadPrivacyTerms(widgetStore.order.merchantTermsLink)"
                >{{ widgetStore.order.merchantName }}'s</a
              >
            </span>
            terms and conditions
          </label>
        </div>
        <div class="pt-2">
          <RouterLink :to="{ name: 'WidgetSignIn' }" @click="onActivate">
            <button
              variant="outline-secondary"
              class="bg-brand-600 rounded w-full h-10 text-base-white hover:bg-brand-700 disabled:opacity-60 disabled:bg-brand-600"
              :disabled="!duration || !billingDay || !acceptTermsAndConditions"
            >
              Accept
            </button>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SelectInput from '@/components/common/SelectInput.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import type { IPaymentPlanPreview, ITrimmedPaymentPlan } from '@/models/paymentPlans';
import type { IPayment } from '@/models/payments';
import router from '@/router';
import * as PaymentPlanService from '@/services/paymentPlans';
import { useWidgetStore } from '@/stores/widget';
import { currency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { determineBillingDay } from '@/utils/paymentPlan.utils';
import { nthNumber, pluralOrSingularText } from '@/utils/text.utils';
import { ChevronRight } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import WidgetMerchantLogo from '../../common/WidgetMerchantLogo.vue';

const isLoading = ref(false);
const errorMessage = ref('');

const widgetStore = useWidgetStore();

const route = useRoute();
const orderId = route?.params?.id as string;
const availableDurations = ref([0]);
const duration = ref(0);
const billingDay = ref(0);
const acceptTermsAndConditions = ref(false);
// Metrics
let orderLengthAdjusted = false;

if (route.query.paymentMethod || route.query.amount) {
  useRouter().replace({ name: 'PaymentLink', params: { id: orderId }, query: route.query });
}

const determineDuration = () => {
  if (availableDurations.value) {
    if (availableDurations.value.find((d) => d === widgetStore.layby?.duration)) {
      return widgetStore.layby?.duration;
    }
    return availableDurations.value[availableDurations.value.length - 1];
  }
  return 0;
};

const init = async () => {
  isLoading.value = true;
  try {
    billingDay.value = determineBillingDay(widgetStore.layby.billingDay);
    await getPaymentPlanPreview(orderId, billingDay.value - 1);
    duration.value = determineDuration();
  } catch (error: any) {
    createException(error);
    errorMessage.value = 'Could not fetch order details. Please refresh to try again.';
  }
  isLoading.value = false;
};

const getPaymentPlanPreview = async (id: string, billingDay: number) => {
  try {
    const response = await PaymentPlanService.getPaymentPlanPreview(id, 'MONTHLY', billingDay);
    widgetStore.paymentPlanPreview = response as IPaymentPlanPreview;
    availableDurations.value = response.paymentPlans.map((pp: any) => pp.quantity);

    return response;
  } catch (error: any) {
    createException(error);
    errorMessage.value = 'Could not fetch payment plan. Please try again.';
  }
};

const initialisePaymentPlan = (quantity: number, billingDay?: number) => {
  widgetStore.layby = {
    duration: quantity,
    billingDay: billingDay!,
  };
};

const durationOptions = computed(() => {
  const options = availableDurations.value.map((i) => {
    const duration = i;
    return { value: duration, text: `${duration} ${pluralOrSingularText(duration, 'month')}` };
  });
  return options;
});

const monthlyBillingDays = computed(() => {
  return Array.from(Array(28).keys()).map((i) => {
    return { value: i + 1, text: `${i + 1} ` + nthNumber(i + 1) };
  });
});

const instalmentAmount = computed(() => {
  const payments = widgetStore?.paymentPlanPreview?.paymentPlans?.filter((pp) => pp.quantity === duration.value)[0]
    ?.payments as unknown as IPayment[];
  if (payments) {
    return currency(payments[1].amount);
  }
  return currency(0);
});

const depositDue = computed(() => {
  const currentPlan = widgetStore?.paymentPlanPreview?.paymentPlans?.filter(
    (pp) => pp.quantity === duration.value,
  )[0] as unknown as ITrimmedPaymentPlan;
  if (currentPlan) {
    return currency(currentPlan.deposit);
  }
  if (widgetStore?.paymentPlanPreview?.depositDue) {
    return currency(widgetStore?.paymentPlanPreview?.depositDue);
  }
  return currency(0);
});

watch(duration, (newValue) => {
  initialisePaymentPlan(parseInt(newValue.toString()), parseInt(billingDay.value.toString()));
});

watch(billingDay, async (newValue) => {
  isLoading.value = true;
  widgetStore.paymentPlanPreview = (await getPaymentPlanPreview(
    orderId!?.toString(),
    parseInt(newValue.toString()) - 1,
  )) as IPaymentPlanPreview;
  const maximumDuration = availableDurations.value[availableDurations.value.length - 1];
  if (duration.value > maximumDuration) {
    orderLengthAdjusted = true;
    duration.value = maximumDuration;
  }
  initialisePaymentPlan(duration.value, newValue);
  isLoading.value = false;
});

const onActivate = () => {
  addMetric('activate_order', {
    order_adjusted: `${orderLengthAdjusted}`,
    order_length: `${duration.value}`,
    billing_day: `${billingDay.value}`,
    channel: 'widget',
  });
};

const onDownloadPrivacyTerms = (termsURL?: string) => {
  window.open(termsURL, '_blank');
};

init();
</script>
